import React, { useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import TestPage from "./pages/TestPage";
import ResultPage from "./pages/ResultPage";
import JoinSession from "./pages/JoinSession";
import TrainerDashboard from "./pages/TrainerDashboard";
import AdminDashboard from "./pages/AdminDashboard";

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="animate-spin gold-text" size={32} />
  </div>
);

function AuthCallback() {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    (async () => {
      if (!sessionId) return navigate("/login", { replace: true });
      try {
        const user = await googleLogin(sessionId);
        window.history.replaceState(null, "", window.location.pathname);
        const home = user.role === "admin" ? "/admin" : user.role === "trainer" ? "/trainer" : "/test";
        navigate(home, { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [googleLogin, navigate]);

  return <FullScreenLoader />;
}

function TrainerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "trainer" && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<Navigate to="/trainer" replace />} />
      <Route path="/test" element={<TestPage />} />
      <Route path="/result/:resultId" element={<ResultPage />} />
      <Route path="/join/:code" element={<JoinSession />} />
      <Route path="/trainer" element={<TrainerRoute><TrainerDashboard /></TrainerRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
